/** Closed Generate flow layer. One particle, one path, N copies. No physics. */

export const FLOW_PARTICLES = [
  "leaf",
  "ember",
  "drop",
  "flake",
  "spark",
  "mist",
  "petal",
  "clump",
] as const;

export type FlowParticle = (typeof FLOW_PARTICLES)[number];

export const FLOW_PATHS = ["fall", "rise", "stream", "drift", "shear"] as const;

export type FlowPath = (typeof FLOW_PATHS)[number];

export const FLOW_SCAFFOLDS = [
  "rain",
  "sparkles",
  "orbs",
  "aurora",
  "mesh",
  "stars",
  "flame",
  "ribbons",
  "hex",
  "leaves",
  "stream",
  "mist",
] as const;

export type FlowScaffold = (typeof FLOW_SCAFFOLDS)[number];

export type MotionFlow = {
  particle: FlowParticle;
  size: number;
  density: number;
  path: FlowPath;
  speed: number;
  scaffold: FlowScaffold;
  sprite?: string;
};

export type FlowInstance = {
  key: string;
  x: number;
  y: number;
  delay: string;
  rotate: number;
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function isParticle(v: string): v is FlowParticle {
  return (FLOW_PARTICLES as readonly string[]).includes(v);
}

function isPath(v: string): v is FlowPath {
  return (FLOW_PATHS as readonly string[]).includes(v);
}

function isScaffold(v: string): v is FlowScaffold {
  return (FLOW_SCAFFOLDS as readonly string[]).includes(v);
}

function legalSprite(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const s = raw.trim();
  if (s.length < 12 || s.length > 80_000) return undefined;
  if (s.startsWith("data:image/") || s.startsWith("blob:") || /^https:\/\//i.test(s)) {
    return s;
  }
  return undefined;
}

export function flowCount(density: number): number {
  const d = clamp(Number.isFinite(density) ? density : 0.5, 0, 1);
  return 8 + Math.round(d * 56);
}

export function scaffoldFor(particle: FlowParticle, path: FlowPath, brief = ""): FlowScaffold {
  const t = (brief || "").toLowerCase();
  if (/aurora/.test(t)) return "aurora";
  if (/mesh|gradient|wash/.test(t)) return "mesh";
  if (/\borbs?\b/.test(t)) return "orbs";
  if (/ribbon/.test(t)) return "ribbons";
  if (/hex|honey/.test(t)) return "hex";
  if (/\bstars?\b/.test(t) && !/spark/.test(t)) return "stars";
  if (particle === "leaf" || particle === "petal" || particle === "flake") return "leaves";
  if (particle === "ember") return "flame";
  if (particle === "mist") return "mist";
  if (particle === "clump") return "hex";
  if (particle === "drop" && (path === "stream" || path === "shear")) return "stream";
  if (particle === "drop") return "rain";
  if (particle === "spark" && (path === "shear" || path === "stream")) return "stream";
  if (particle === "spark") return "sparkles";
  return "leaves";
}

export function seedFlowFromBrief(
  brief: string,
): Pick<MotionFlow, "particle" | "path" | "density" | "size" | "scaffold"> {
  const t = (brief || "").toLowerCase();
  if (/aurora/.test(t)) {
    return { particle: "mist", path: "drift", density: 0.5, size: 28, scaffold: "aurora" };
  }
  if (/mesh|gradient/.test(t)) {
    return { particle: "mist", path: "drift", density: 0.4, size: 24, scaffold: "mesh" };
  }
  if (/\borbs?\b/.test(t)) {
    return { particle: "mist", path: "drift", density: 0.45, size: 32, scaffold: "orbs" };
  }
  if (/leaf|leaves/.test(t)) {
    return { particle: "leaf", path: "fall", density: 0.72, size: 22, scaffold: "leaves" };
  }
  if (/petal/.test(t)) {
    return { particle: "petal", path: "fall", density: 0.65, size: 18, scaffold: "leaves" };
  }
  if (/fire|lava|ember|magma|volcano|flame/.test(t)) {
    return { particle: "ember", path: "rise", density: 0.78, size: 10, scaffold: "flame" };
  }
  if (/rain|\bdrop\b/.test(t)) {
    return { particle: "drop", path: "fall", density: 0.8, size: 36, scaffold: "rain" };
  }
  if (/\bsnow\b|flake/.test(t)) {
    return { particle: "flake", path: "fall", density: 0.7, size: 14, scaffold: "leaves" };
  }
  if (/\bstars?\b/.test(t)) {
    return { particle: "spark", path: "drift", density: 0.7, size: 8, scaffold: "stars" };
  }
  if (/spark/.test(t)) {
    return { particle: "spark", path: "rise", density: 0.7, size: 8, scaffold: "sparkles" };
  }
  if (/steam|mist|fog/.test(t)) {
    return { particle: "mist", path: "rise", density: 0.55, size: 40, scaffold: "mist" };
  }
  if (/wind|gale|breeze/.test(t)) {
    return { particle: "spark", path: "shear", density: 0.7, size: 18, scaffold: "stream" };
  }
  if (/ribbon/.test(t)) {
    return { particle: "mist", path: "stream", density: 0.5, size: 24, scaffold: "ribbons" };
  }
  if (/hex|honey/.test(t)) {
    return { particle: "clump", path: "drift", density: 0.5, size: 28, scaffold: "hex" };
  }
  if (/water|river|kelp|ocean|wave/.test(t)) {
    return { particle: "drop", path: "stream", density: 0.72, size: 14, scaffold: "stream" };
  }
  return { particle: "leaf", path: "drift", density: 0.5, size: 18, scaffold: "orbs" };
}

export function parseMotionFlow(raw: unknown, brief = ""): MotionFlow {
  const seeded = seedFlowFromBrief(brief);
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const particleRaw = String(o.particle || "");
  const pathRaw = String(o.path || "");
  const size = Number(o.size);
  const density = Number(o.density);
  const speed = Number(o.speed);
  const particle = isParticle(particleRaw) ? particleRaw : seeded.particle;
  const path = isPath(pathRaw) ? pathRaw : seeded.path;
  const scaffoldRaw = String(o.scaffold || "");
  return {
    particle,
    path,
    size: Number.isFinite(size) ? clamp(Math.round(size), 8, 48) : seeded.size,
    density: Number.isFinite(density) ? clamp(density, 0, 1) : seeded.density,
    speed: Number.isFinite(speed) ? clamp(speed, 0.25, 2) : 1,
    scaffold: isScaffold(scaffoldRaw) ? scaffoldRaw : scaffoldFor(particle, path, brief) || seeded.scaffold,
    sprite: legalSprite(o.sprite),
  };
}

export function defaultFlow(brief: string): MotionFlow {
  const seeded = seedFlowFromBrief(brief);
  return {
    particle: seeded.particle,
    path: seeded.path,
    size: seeded.size,
    density: seeded.density,
    speed: 1,
    scaffold: seeded.scaffold,
  };
}

function svgData(body: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">${body}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function builtinStamp(particle: FlowParticle): string {
  if (particle === "ember") {
    return svgData(
      `<circle cx="16" cy="18" r="8" fill="#fb923c"/><circle cx="16" cy="14" r="5" fill="#fbbf24"/>`,
    );
  }
  if (particle === "drop") {
    return svgData(
      `<path d="M16 4 C16 4 6 16 6 22 a10 10 0 0 0 20 0 C26 16 16 4 16 4z" fill="#38bdf8"/>`,
    );
  }
  if (particle === "flake") {
    return svgData(
      `<g stroke="#e0f2fe" stroke-width="2" fill="none" stroke-linecap="round">
        <path d="M16 4v24M4 16h24M8 8l16 16M24 8L8 24"/>
      </g>`,
    );
  }
  if (particle === "spark") {
    return svgData(
      `<path d="M16 2l2.4 9.2L28 16l-9.6 4.8L16 30l-2.4-9.2L4 16l9.6-4.8z" fill="#fde68a"/>`,
    );
  }
  if (particle === "mist") {
    return svgData(
      `<ellipse cx="16" cy="16" rx="12" ry="8" fill="#ffffff" fill-opacity=".55"/>`,
    );
  }
  if (particle === "petal") {
    return svgData(
      `<ellipse cx="16" cy="16" rx="7" ry="13" fill="#f9a8d4" transform="rotate(-20 16 16)"/>`,
    );
  }
  if (particle === "clump") {
    return svgData(
      `<circle cx="13" cy="18" r="8" fill="#92400e"/><circle cx="20" cy="16" r="6" fill="#a16207"/>`,
    );
  }
  return svgData(
    `<path d="M18 4 C10 8 8 18 14 26 C12 18 16 12 26 10 C18 12 16 8 18 4z" fill="#84cc16"/>`,
  );
}

export function flowStamp(flow: MotionFlow): string {
  return flow.sprite || builtinStamp(flow.particle);
}

export function pathDurationSec(path: FlowPath, speed: number): number {
  const s = clamp(speed || 1, 0.25, 2);
  const base =
    path === "fall"
      ? 2.8
      : path === "rise"
        ? 3.2
        : path === "stream"
          ? 3.6
          : path === "shear"
            ? 4.4
            : 8;
  return base / s;
}

export function flowInstances(flow: MotionFlow): FlowInstance[] {
  const n = flowCount(flow.density);
  const out: FlowInstance[] = [];
  const sc = flow.scaffold;
  const path = flow.path;
  for (let i = 0; i < n; i++) {
    const seed = (i + 1) * 47 + Math.round(flow.density * 13) + flow.size;
    let x = (seed * 13) % 94 + 3;
    let y = (seed * 29) % 88 + 4;
    if (sc === "rain" || sc === "leaves" || path === "fall") {
      x = (seed * 37) % 100;
      y = -10 - (i % 8);
    } else if (sc === "sparkles" || sc === "flame" || path === "rise") {
      x = (seed * 53) % 100;
      y = 102 + (i % 10);
    } else if (sc === "stream" || path === "stream" || path === "shear") {
      x = -8 - (i % 6);
      y = (seed * 29) % 86 + 6;
    }
    out.push({
      key: `flow-${i}`,
      x,
      y,
      delay: `${((i * 0.23) % 3.8).toFixed(2)}s`,
      rotate: (seed * 17) % 50 - 25,
    });
  }
  return out;
}

export function FLOW_KEYFRAMES_CSS(): string {
  return [
    `@keyframes sf-flow-fall{from{transform:translate3d(0,-20%,0) rotate(-25deg)}to{transform:translate3d(8%,110vh,0) rotate(50deg)}}`,
    `@keyframes sf-flow-rise{from{transform:translate3d(0,40%,0);opacity:.25}to{transform:translate3d(-6%,-90vh,0) scale(1.15);opacity:0}}`,
    `@keyframes sf-flow-stream{from{transform:translate3d(-20%,0,0)}to{transform:translate3d(80vw,8%,0)}}`,
    `@keyframes sf-flow-drift{from{transform:translate3d(-4%,2%,0)}to{transform:translate3d(6%,-4%,0)}}`,
    `@keyframes sf-flow-shear{from{transform:translate3d(-30%,0,0) skewX(-6deg)}to{transform:translate3d(40vw,0,0) skewX(6deg)}}`,
    `.sf-flow-p{position:absolute;z-index:2;pointer-events:none;will-change:transform;object-fit:contain}`,
    `.sf-flow-p[data-path=fall]{animation-name:sf-flow-fall;animation-timing-function:linear;animation-iteration-count:infinite}`,
    `.sf-flow-p[data-path=rise]{animation-name:sf-flow-rise;animation-timing-function:ease-in-out;animation-iteration-count:infinite}`,
    `.sf-flow-p[data-path=stream]{animation-name:sf-flow-stream;animation-timing-function:linear;animation-iteration-count:infinite}`,
    `.sf-flow-p[data-path=drift]{animation-name:sf-flow-drift;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
    `.sf-flow-p[data-path=shear]{animation-name:sf-flow-shear;animation-timing-function:linear;animation-iteration-count:infinite}`,
    `@media (prefers-reduced-motion:reduce){.sf-flow-p{animation:none}}`,
  ].join("\n");
}
